import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/basic/button";
import InlineEditRow from "components/basic/inlineEditRow";
import {
  formatElectricalLicenseNumber,
  formatIsoDate,
  formatPhoneNumber,
} from "pages/formTools";
import InlineFileEditRow from "components/basic/inlineFileEditRow";
import Typography from "components/basic/typography";
import { useNavigate } from "react-router-dom";
import SignUpReviewLayout from "../../components/SignUpReviewLayout";
import { useSnack } from "hooks/useSnack";
import { useCallback, useState } from "react";
import { useRegisterAccountWithVendorMutation } from "generated/graphql";
import { useS3 } from "hooks/useS3";
import { Account, SignUpFormData, Vendor } from "../..";
import { clearSignUpDraft } from "../../helpers/signUpDraft";
import ReviewRow from "./components/ReviewRow";
import DeclarationConsent from "./components/DeclarationConsent";
import { ORGANIZATION_TYPES } from "../../constants";
import {
  notificationEmailChoices,
  selectedNotificationEmail,
} from "../../helpers/notificationEmails";

// ─── Section header ────────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="h5"
      style={{
        fontWeight: "bold",
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(0.5),
        borderBottom: `2px solid ${theme.palette.warning.main}`,
        paddingBottom: theme.spacing(0.5),
      }}
    >
      {title}
    </Typography>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface VendorReviewFormProps {
  signUpData: SignUpFormData;
}

const VendorReviewForm: React.FC<VendorReviewFormProps> = ({ signUpData }) => {
  const { account, setAccount, vendor, setVendor } = signUpData;
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const { t: tAccount } = useTranslation("account");
  const yesNo = (value: string) =>
    value === "Yes" ? tAccount("yes") : value === "No" ? tAccount("no") : value;
  const { uploadToS3 } = useS3();
  const [, registerAccountWithVendor] = useRegisterAccountWithVendorMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConsentErrors, setShowConsentErrors] = useState(false);

  const setA = (field: keyof Account) => (val: string) =>
    setAccount((prev) => ({ ...prev, [field]: val }));

  const setV = (field: keyof Vendor) => (val: string) =>
    setVendor((prev) => ({ ...prev, [field]: val }));

  const handleCreateAccount = useCallback(async () => {
    if (!vendor.declarationAccepted || !vendor.termsAccepted) {
      setShowConsentErrors(true);
      showSnack(t("review.accept_required"), "error");
      return;
    }

    setIsSubmitting(true);
    try {
      let proofOfInsuranceUrl: string | null =
        vendor.proofOfInsuranceURL || null;
      let businessLicenseUrl: string | null = vendor.businessLicenseURL || null;
      let electricalLicenseUrl: string | null =
        vendor.electricalLicenseURL || null;

      if (vendor.proofOfInsurance) {
        proofOfInsuranceUrl = await uploadToS3(
          vendor.proofOfInsurance,
          "proof-of-insurance",
        );
        if (!proofOfInsuranceUrl) {
          showSnack(t("review.insurance_upload_failed"), "error");
          return;
        }
      }
      if (vendor.businessLicense) {
        businessLicenseUrl = await uploadToS3(
          vendor.businessLicense,
          "business-licenses",
        );
        if (!businessLicenseUrl) {
          showSnack(t("review.license_upload_failed"), "error");
          return;
        }
      }
      if (vendor.electricalLicense && vendor.offeringEvInstallation === "Yes") {
        electricalLicenseUrl = await uploadToS3(
          vendor.electricalLicense,
          "electrical-licenses",
        );
        if (!electricalLicenseUrl) {
          showSnack(t("review.electrical_upload_failed"), "error");
          return;
        }
      }

      const response = await registerAccountWithVendor(
        {
          accountInput: {
            email: account.email,
            password: account.password,
            firstName: account.firstName,
            lastName: account.lastName,
            userType: account.userType,
            googleAccessToken: account.googleAccessToken || undefined,
            profilePhotoUrl: account.profilePhotoUrl || undefined,
            phoneNumber: account.phoneNumber,
            address: account.address,
            latitude: account.latitude ?? undefined,
            longitude: account.longitude ?? undefined,
            placeId: account.placeId ?? undefined,
            onboardingCompleted: true,
          },
          vendorInput: {
            businessName: vendor.businessName,
            operatingAs: vendor.operatingAs || undefined,
            mailingSameAsBusiness: vendor.mailingSameAsBusiness,
            mailingAddress: vendor.mailingSameAsBusiness
              ? null
              : vendor.mailingAddress,
            organizationType: vendor.organizationType,
            primaryContactFirstName: vendor.primaryContactFirstName,
            primaryContactLastName: vendor.primaryContactLastName,
            primaryContactPosition: vendor.primaryContactPosition,
            primaryContactEmail: vendor.primaryContactEmail,
            primaryContactPhone: vendor.primaryContactPhone,
            notificationEmail: selectedNotificationEmail(account.email, vendor),
            primaryContactIsSignatory: vendor.primaryContactIsSignatory,
            signatoryFirstName: vendor.signatoryFirstName,
            signatoryLastName: vendor.signatoryLastName,
            signatoryPosition: vendor.signatoryPosition,
            signatoryEmail: vendor.signatoryEmail,
            signatoryPhone: vendor.signatoryPhone,
            businessNumber: vendor.businessNumber,
            proofOfInsuranceURL: proofOfInsuranceUrl!,
            insuranceExpiryDate: vendor.insuranceExpiryDate,
            businessLicenseURL: businessLicenseUrl!,
            offeringEvInstallation: vendor.offeringEvInstallation,
            province: vendor.province,
            ...(vendor.offeringEvInstallation === "Yes"
              ? {
                  electricalLicenseURL: electricalLicenseUrl,
                  electricalLicenseNumber: vendor.electricalLicenseNumber,
                  electricalLicenseExpiryDate:
                    vendor.electricalLicenseExpiryDate,
                }
              : {}),
            declarationAccepted: vendor.declarationAccepted,
            termsAccepted: vendor.termsAccepted,
          },
        },
        { requestPolicy: "network-only" },
      );

      if (response.data?.registerAccountWithVendor.errors) {
        const errorMsg = response.data.registerAccountWithVendor.errors
          .map((e) => e.message)
          .join(", ");
        showSnack(errorMsg || t("common.failed_try_later"), "error");
        return;
      }

      clearSignUpDraft();
      showSnack(t("common.registered"), "success");
      navigate("/dashboard/onboarding");
    } catch (error) {
      console.error("Registration error:", error);
      showSnack(t("common.unexpected"), "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    t,
    account,
    vendor,
    registerAccountWithVendor,
    uploadToS3,
    navigate,
    showSnack,
  ]);

  return (
    <SignUpReviewLayout maxWidth={640}>
      <Typography
        variant="h4"
        align="center"
        style={{ fontWeight: "bold", marginBottom: theme.spacing(3) }}
      >
        {t("review.title")}
      </Typography>

      {/* ── PERSONAL ACCOUNT ─────────────────────────────────────────── */}
      <SectionHeader title={t("review.account")} />
      <ReviewRow label={t("review.email")} value={account.email} />

      <SectionHeader title={t("review.name")} />
      <InlineEditRow
        label={t("review.first_name")}
        value={account.firstName}
        onSave={setA("firstName")}
      />
      <InlineEditRow
        label={t("review.last_name")}
        value={account.lastName}
        onSave={setA("lastName")}
      />

      <SectionHeader title={t("review.contact_details")} />
      <InlineEditRow
        label={t("review.phone")}
        value={account.phoneNumber}
        onSave={setA("phoneNumber")}
        mask={formatPhoneNumber}
      />
      <InlineEditRow
        label={t("review.address")}
        value={account.address}
        onSave={setA("address")}
      />

      {/* ── BUSINESS INFO ─────────────────────────────────────────────── */}
      <SectionHeader title={t("review.business")} />
      <InlineEditRow
        label={t("review.business_name")}
        value={vendor.businessName}
        onSave={(businessName) =>
          setVendor((prev) => ({
            ...prev,
            businessName,
            ...(prev.operatingAsSameAsLegal
              ? { operatingAs: businessName }
              : {}),
          }))
        }
      />
      <InlineEditRow
        label={t("review.operating_as")}
        value={vendor.operatingAs ?? ""}
        onSave={(operatingAs) =>
          setVendor((prev) => ({
            ...prev,
            operatingAs,
            operatingAsSameAsLegal: operatingAs === prev.businessName,
          }))
        }
      />
      <InlineEditRow
        label={t("review.business_address")}
        value={account.address}
        onSave={setA("address")}
      />
      <InlineEditRow
        label={t("review.mailing_address")}
        value={
          vendor.mailingSameAsBusiness
            ? account.address
            : (vendor.mailingAddress ?? "")
        }
        onSave={(mailingAddress) =>
          setVendor((prev) => ({
            ...prev,
            mailingAddress,
            mailingSameAsBusiness: mailingAddress === account.address,
          }))
        }
      />

      {/* ── PRIVACY & CONTACTS ────────────────────────────────────────── */}
      <SectionHeader title={t("review.privacy_contacts")} />
      <InlineEditRow
        label={t("review.organization_type")}
        value={vendor.organizationType}
        onSave={setV("organizationType")}
        inputType="dropdown"
        options={ORGANIZATION_TYPES.map(({ label }) => label)}
        formatOption={(value) => {
          const type = ORGANIZATION_TYPES.find(({ label }) => label === value);
          return type ? tAccount(type.labelKey) : value;
        }}
      />
      <InlineEditRow
        label={t("review.primary_first")}
        value={vendor.primaryContactFirstName}
        onSave={setV("primaryContactFirstName")}
      />
      <InlineEditRow
        label={t("review.primary_last")}
        value={vendor.primaryContactLastName}
        onSave={setV("primaryContactLastName")}
      />
      <InlineEditRow
        label={t("review.primary_position")}
        value={vendor.primaryContactPosition}
        onSave={setV("primaryContactPosition")}
      />
      <InlineEditRow
        label={t("review.primary_email")}
        value={vendor.primaryContactEmail}
        onSave={setV("primaryContactEmail")}
      />
      <InlineEditRow
        label={t("review.primary_phone")}
        value={vendor.primaryContactPhone}
        onSave={setV("primaryContactPhone")}
        mask={formatPhoneNumber}
      />
      <InlineEditRow
        label={t("review.notification_email")}
        value={selectedNotificationEmail(account.email, vendor)}
        onSave={setV("notificationEmail")}
        inputType="dropdown"
        options={notificationEmailChoices(account.email, vendor)}
      />
      <InlineEditRow
        label={t("review.primary_can_sign")}
        value={vendor.primaryContactIsSignatory ? "Yes" : "No"}
        onSave={(value) =>
          setVendor((prev) => ({
            ...prev,
            primaryContactIsSignatory: value === "Yes",
          }))
        }
        inputType="dropdown"
        options={["Yes", "No"]}
        formatOption={yesNo}
      />
      {!vendor.primaryContactIsSignatory && (
        <>
          <InlineEditRow
            label={t("review.signatory_first")}
            value={vendor.signatoryFirstName ?? ""}
            onSave={setV("signatoryFirstName")}
          />
          <InlineEditRow
            label={t("review.signatory_last")}
            value={vendor.signatoryLastName ?? ""}
            onSave={setV("signatoryLastName")}
          />
          <InlineEditRow
            label={t("review.signatory_position")}
            value={vendor.signatoryPosition ?? ""}
            onSave={setV("signatoryPosition")}
          />
          <InlineEditRow
            label={t("review.signatory_email")}
            value={vendor.signatoryEmail ?? ""}
            onSave={setV("signatoryEmail")}
          />
          <InlineEditRow
            label={t("review.signatory_phone")}
            value={vendor.signatoryPhone ?? ""}
            onSave={setV("signatoryPhone")}
            mask={formatPhoneNumber}
          />
        </>
      )}

      {/* ── BUSINESS REGISTRATION ─────────────────────────────────────── */}
      <SectionHeader title={t("review.registration")} />
      <InlineEditRow
        label={t("review.business_number")}
        value={vendor.businessNumber ?? ""}
        onSave={setV("businessNumber")}
      />
      <InlineFileEditRow
        label={t("review.proof_insurance")}
        file={vendor.proofOfInsurance}
        url={vendor.proofOfInsuranceURL}
        uploadLabel={t("review.upload_insurance")}
        onSave={(file) =>
          setVendor((prev) => ({ ...prev, proofOfInsurance: file }))
        }
      />
      <ReviewRow
        label={t("review.insurance_expiry")}
        value={formatIsoDate(vendor.insuranceExpiryDate)}
      />
      <InlineFileEditRow
        label={t("review.business_license")}
        file={vendor.businessLicense}
        url={vendor.businessLicenseURL}
        uploadLabel={t("review.upload_business_license")}
        onSave={(file) =>
          setVendor((prev) => ({ ...prev, businessLicense: file }))
        }
      />

      {/* ── SIGNATURE & INSTALLATION ──────────────────────────────────── */}
      <SectionHeader title={t("review.signature_installation")} />
      <InlineEditRow
        label={t("review.offering")}
        value={vendor.offeringEvInstallation}
        onSave={setV("offeringEvInstallation")}
        inputType="dropdown"
        options={["Yes", "No"]}
        formatOption={yesNo}
      />
      <InlineEditRow
        label={t("review.province")}
        value={vendor.province}
        onSave={setV("province")}
      />
      {vendor.offeringEvInstallation === "Yes" && (
        <>
          <InlineFileEditRow
            label={t("review.electrical_license")}
            file={vendor.electricalLicense}
            url={vendor.electricalLicenseURL}
            uploadLabel={t("review.upload_electrical_license")}
            onSave={(file) =>
              setVendor((prev) => ({ ...prev, electricalLicense: file }))
            }
          />
          <InlineEditRow
            label={t("review.license_number")}
            value={vendor.electricalLicenseNumber ?? ""}
            onSave={setV("electricalLicenseNumber")}
            mask={formatElectricalLicenseNumber}
          />
          <ReviewRow
            label={t("review.license_expiry")}
            value={formatIsoDate(vendor.electricalLicenseExpiryDate)}
          />
        </>
      )}
      <SectionHeader title={t("review.declaration")} />
      <DeclarationConsent
        declarationAccepted={vendor.declarationAccepted}
        termsAccepted={vendor.termsAccepted}
        onChange={(patch) => setVendor((prev) => ({ ...prev, ...patch }))}
        showErrors={showConsentErrors}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(2),
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(4),
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          style={{
            color: theme.palette.common.white,
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          onClick={() => navigate("?currentPage=signatureForm")}
          disabled={isSubmitting}
        >
          {t("common.back")}
        </Button>

        <Button
          variant="contained"
          color="warning"
          size="large"
          style={{
            color: theme.palette.common.white,
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          onClick={handleCreateAccount}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("common.create_account")}
        </Button>
      </div>
    </SignUpReviewLayout>
  );
};

export default VendorReviewForm;
